/**
 * REST side of the Agent Manager file API (PSTUDIO-3336). The list queries and the
 * delete mutation are GraphQL and live in `src/gql/agent-manager.gql`; only writes,
 * single-file read and the version archive are REST.
 *
 *   POST /rest/agent-manager-sync/:solutionId                       (add/update/delete)
 *   GET  /rest/agent-manager-file/:solutionId/:versionName/*        (raw bytes)
 *   GET  /rest/agent-manager-archive/:solutionId/:versionName       (zip stream)
 *
 * Writes go through `agent-manager-sync`, NOT the older
 * `/rest/agent-manager/:organisationName/:projectId/:repositoryName/:branch/:type`. That
 * route still exists, but it derives the repository path itself from the uploaded
 * file's name (`getFilePathByType`) and takes every file under the single multipart field
 * `files`, so it cannot express an update-with-sha or a delete. The sync route takes an
 * explicit path per change plus the sha the client last saw, which is what makes
 * concurrent edits detectable instead of silently clobbering.
 *
 * Replaces the retired Bot Manager rail (`/rest/bot-manager/...`).
 */

const CONFIG_FILE = 'config.json';
const PACKAGE_FILE = 'package.json';

/** Mirrors `GiteaAssetsTypesEnum` on the backend. */
export type AgentManagerFileType =
  | 'assets'
  | 'config'
  | 'custom-assets'
  | 'knowledge'
  | 'scripts'
  | 'templates';

/**
 * Repository folder per type. The client owns the full path now, so this mirrors the
 * backend's `getFilePathByType` map and the `filePath` each list resolver reads.
 *
 * Note `scripts` → `services/`: the type and the folder do NOT share a name, and
 * `scriptsFiles` lists `services/`. `config` sits at the repository root.
 */
const FOLDER_BY_TYPE: Record<AgentManagerFileType, string> = {
  assets: 'assets/',
  config: '',
  'custom-assets': 'custom-assets/',
  knowledge: 'knowledge/',
  scripts: 'services/',
  templates: 'templates/',
};

/** The repository path a file of this type, at this relative path, is written to. */
export const buildAgentManagerPath = (
  type: AgentManagerFileType,
  relativePath: string
): string => `${FOLDER_BY_TYPE[type]}${relativePath}`;

/**
 * The inverse of {@link buildAgentManagerPath}: a listed file's repository path, relative
 * to its type's folder. What an edit is written back with, so it lands on the file it was
 * opened from — rebuilding it from the display name moved a nested file to the folder root.
 */
export const readTypeRelativePath = (
  type: AgentManagerFileType,
  path: string
): string => {
  const folder = FOLDER_BY_TYPE[type];
  return folder && path.startsWith(folder) ? path.slice(folder.length) : path;
};

/** One picked file plus its path relative to the upload root, e.g. `src/index.ts`. */
export type PickedAssetFile = { relativePath: string; file: File };

export type AssetValidationResult =
  | { ok: true }
  | { ok: false; reason: string };

const restBase = (): string => import.meta.env.FE_ADMIN_URL;

/**
 * The write endpoint. Takes only the solution: the organisation is derived server-side
 * from the solution's project, and the version arrives as `metadata.branch` rather than
 * as a path segment.
 */
export const buildSyncUrl = (solutionId: string): string =>
  `${restBase()}/rest/agent-manager-sync/${encodeURIComponent(solutionId)}`;

/**
 * The file path is a multi-segment wildcard, so its slashes must survive — encode each
 * segment individually rather than the whole path.
 */
export const buildFileUrl = ({
  solutionId,
  versionName,
  path,
}: {
  solutionId: string;
  versionName: string;
  path: string;
}): string =>
  `${restBase()}/rest/agent-manager-file/${encodeURIComponent(solutionId)}/${encodeURIComponent(versionName)}/${path
    .split('/')
    .map(encodeURIComponent)
    .join('/')}`;

export const buildArchiveUrl = ({
  solutionId,
  versionName,
}: {
  solutionId: string;
  versionName: string;
}): string =>
  `${restBase()}/rest/agent-manager-archive/${encodeURIComponent(solutionId)}/${encodeURIComponent(versionName)}`;

/**
 * Pulls the base64 payload out of a single-file response.
 *
 * The endpoint is documented only as "will be base64", so both shapes are accepted: a
 * raw base64 body, or JSON wrapping it under a `content`/`data`/`file` key. Once the
 * real shape is confirmed this can collapse to one branch.
 */
export const extractBase64 = (body: string): string => {
  const trimmed = body.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      for (const key of ['content', 'data', 'file', 'base64']) {
        const value = parsed[key];
        if (typeof value === 'string') return value;
      }
    } catch {
      // Not JSON after all — fall through and treat the body as base64.
    }
  }
  return trimmed;
};

const hasFile = (files: PickedAssetFile[], name: string): boolean =>
  files.some((f) => f.relativePath === name);

/**
 * `config.json` is required by the SDK contract. `package.json` is required because
 * `build-custom-assets.ts` SKIPS any asset folder without one — such an asset would
 * upload fine and then silently never build, so it is rejected here instead.
 */
export const validateAssetFolder = (
  files: PickedAssetFile[]
): AssetValidationResult => {
  if (!files.length) {
    return { ok: false, reason: 'No files were selected.' };
  }
  if (!hasFile(files, CONFIG_FILE)) {
    return {
      ok: false,
      reason: `Missing ${CONFIG_FILE} — select the folder produced by \`pypectl asset pack\`.`,
    };
  }
  if (!hasFile(files, PACKAGE_FILE)) {
    return {
      ok: false,
      reason: `Missing ${PACKAGE_FILE} — the asset would upload but never build.`,
    };
  }
  return { ok: true };
};

/**
 * The asset's folder name under `custom-assets/`, read out of its `config.json`.
 *
 * `embed_id` is what the pype-sdk contract names an asset by (`ps-pype-cx-<embed_id>`,
 * packages/pype-sdk/src/config/schema.ts). `name` is not part of that contract and is
 * read only as a fallback for older packs.
 */
export const readAssetConfigName = async (
  files: PickedAssetFile[]
): Promise<string | undefined> => {
  const entry = files.find((f) => f.relativePath === CONFIG_FILE);
  if (!entry) return undefined;
  try {
    const parsed = JSON.parse(await entry.file.text()) as {
      embed_id?: unknown;
      name?: unknown;
    };
    const id = [parsed.embed_id, parsed.name].find(
      (value): value is string =>
        typeof value === 'string' && value.trim() !== ''
    );
    return id?.trim();
  } catch {
    return undefined;
  }
};

const failure = async (response: Response, action: string): Promise<never> => {
  let detail = '';
  try {
    detail = await response.text();
  } catch {
    detail = '';
  }
  throw new Error(
    `${action} failed (${response.status})${detail ? `: ${detail}` : ''}`
  );
};

/** One repository write. `expectedSha` is the sha the client last saw for that path. */
export type AgentManagerChange =
  | { op: 'add'; path: string; file: File }
  | { op: 'update'; path: string; expectedSha: string; file: File }
  | { op: 'delete'; path: string; expectedSha: string };

export type AgentManagerConflictReason =
  | 'already_exists'
  | 'missing'
  | 'sha_mismatch';

export type AgentManagerConflict = {
  path: string;
  op: string;
  reason: AgentManagerConflictReason;
  expectedSha?: string;
  actualSha?: string;
};

/**
 * Nothing was written: the server checks every change against the current tree BEFORE
 * applying any of them, so a single conflict rejects the whole batch.
 */
export class AgentManagerConflictError extends Error {
  constructor(readonly conflicts: AgentManagerConflict[]) {
    super('The files changed since they were loaded.');
    this.name = 'AgentManagerConflictError';
  }
}

/** The batch was malformed — a bad path, a duplicate path, a missing sha. */
export class AgentManagerValidationError extends Error {
  constructor(readonly issues: string[]) {
    super(issues.join('; ') || 'Invalid change set.');
    this.name = 'AgentManagerValidationError';
  }
}

export type AgentManagerSyncResult = {
  messages: string[];
  counts: { added: number; updated: number; deleted: number };
};

/**
 * Turns picked files into changes, choosing `add` or `update` per file by whether the
 * path already exists — an `add` onto an existing path is a conflict, so the distinction
 * is not cosmetic. `existing` is the type's current file list, which carries the sha.
 */
export const buildUploadChanges = ({
  type,
  files,
  existing,
}: {
  type: AgentManagerFileType;
  files: PickedAssetFile[];
  existing: { path?: string | null; sha?: string | null }[];
}): AgentManagerChange[] => {
  const shaByPath = new Map(
    existing
      .filter((file) => file.path && file.sha)
      .map((file) => [file.path as string, file.sha as string])
  );

  return files.map(({ relativePath, file }) => {
    const path = buildAgentManagerPath(type, relativePath);
    const expectedSha = shaByPath.get(path);

    return expectedSha
      ? { op: 'update' as const, path, expectedSha, file }
      : { op: 'add' as const, path, file };
  });
};

/**
 * Applies a batch of changes as one commit.
 *
 * Each file rides under its own multipart field (`file-<n>`), referenced from the change
 * by `fileField` — the field names are plumbing, while the repository path travels
 * explicitly in `metadata`. That is the part that changed: the old route inferred the
 * path from the filename, so it could not target a nested path or carry a sha.
 */
export const syncAgentManagerFiles = async ({
  solutionId,
  versionName,
  changes,
  commitMessage,
}: {
  solutionId: string;
  versionName: string;
  changes: AgentManagerChange[];
  commitMessage?: string;
}): Promise<AgentManagerSyncResult> => {
  const body = new FormData();

  const metadataChanges = changes.map((change, index) => {
    if (change.op === 'delete') {
      return {
        op: change.op,
        path: change.path,
        expectedSha: change.expectedSha,
      };
    }

    const fileField = `file-${index}`;
    // The filename is cosmetic here — `metadata.path` is what decides where it lands —
    // but keep it meaningful for server-side logs.
    body.append(fileField, change.file, change.path.split('/').pop());

    return change.op === 'update'
      ? {
          op: change.op,
          path: change.path,
          expectedSha: change.expectedSha,
          fileField,
        }
      : { op: change.op, path: change.path, fileField };
  });

  body.append(
    'metadata',
    JSON.stringify({
      branch: versionName,
      ...(commitMessage ? { commitMessage } : {}),
      changes: metadataChanges,
    })
  );

  const response = await fetch(buildSyncUrl(solutionId), {
    method: 'POST',
    credentials: 'include',
    body,
  });

  if (response.status === 409) {
    const payload = (await response.json().catch(() => ({}))) as {
      conflicts?: AgentManagerConflict[];
    };
    throw new AgentManagerConflictError(payload.conflicts ?? []);
  }

  if (response.status === 400) {
    const payload = (await response.json().catch(() => ({}))) as {
      issues?: string[];
      error?: string;
    };
    throw new AgentManagerValidationError(
      payload.issues ?? (payload.error ? [payload.error] : [])
    );
  }

  if (!response.ok) await failure(response, 'Saving files');

  // The write succeeded — the status says so. Callers only await this, so an empty or
  // non-JSON success body must not be turned into a failed upload for a write that landed.
  return (await response.json().catch(() => ({
    messages: [],
    counts: { added: 0, updated: 0, deleted: 0 },
  }))) as AgentManagerSyncResult;
};

/**
 * Fetches one file as bytes — the faithful form, for anything that is written to disk.
 *
 * A custom asset folder is not all source code (an icon, a font), and decoding those to
 * text and back corrupts them.
 */
export const fetchAgentManagerFileBytes = async (args: {
  solutionId: string;
  versionName: string;
  path: string;
}): Promise<Uint8Array> =>
  // Through the Blob reader, as bytes. Reading the body as text first (as this did) replaced
  // every invalid UTF-8 byte of a binary file (PSTUDIO-4354).
  new Uint8Array(
    await (await fetchAgentManagerFileBlob(buildFileUrl(args))).arrayBuffer()
  );

/**
 * A file object's `url` as something the browser can request.
 *
 * The backend returns a RELATIVE path (`/rest/agent-manager-file/…`) for a route that lives
 * on the admin host, so used as-is it resolves against Pro Studio's own origin. An absolute
 * url (a legacy asset) is returned unchanged.
 */
export const resolveAgentManagerUrl = (url: string): string =>
  url.startsWith('/') ? `${restBase()}${url}` : url;

/**
 * Fetches a file by its `url` as a typed Blob — for a Files → Media preview (PSTUDIO-4354),
 * and under `fetchAgentManagerFileBytes` for everything else.
 *
 * The body IS the file: the handler (backend `agent-manager-file.ts`, since c87802262)
 * decodes the stored base64 itself and sends the raw bytes with the file's own
 * `Content-Type`. So it is read as bytes and never decoded again — guessing an encoding from
 * the content turned binaries, config files and word lists into garbage (PSTUDIO-4354).
 */
export const fetchAgentManagerFileBlob = async (
  url: string,
  mimeType?: string | null
): Promise<Blob> => {
  const response = await fetch(resolveAgentManagerUrl(url), {
    credentials: 'include',
  });
  if (!response.ok) await failure(response, `Reading ${url}`);

  const contentType =
    response.headers.get('Content-Type')?.split(';')[0].trim() ?? '';
  return new Blob([await response.arrayBuffer()], {
    type: mimeType || contentType,
  });
};

/** Fetches one file and decodes it to text, for the editor. */
export const fetchAgentManagerFileText = async (args: {
  solutionId: string;
  versionName: string;
  path: string;
}): Promise<string> =>
  new TextDecoder().decode(await fetchAgentManagerFileBytes(args));
